import { RUNTIME_BACKEND_URL } from '../../lib/backendUrl';
import { useEffect, useState } from 'react';
import { useAuthStore } from '../../store/authStore';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  FileText,
  Plus,
  Edit,
  Trash2,
  Star,
  Loader2,
  Info
} from 'lucide-react';

const API = RUNTIME_BACKEND_URL;

// Kullanılabilir placeholder'lar
const placeholders = [
  { key: '{bride_name}', description: 'Gelin adı' },
  { key: '{groom_name}', description: 'Damat adı' },
  { key: '{wedding_date}', description: 'Düğün tarihi' },
  { key: '{services}', description: 'Seçilen hizmetler' },
  { key: '{total_price}', description: 'Toplam tutar' },
  { key: '{deposit_amount}', description: 'Kapora tutarı' },
  { key: '{remaining_amount}', description: 'Kalan tutar' },
];

export default function AdminTemplatesPage() {
  const { token } = useAuthStore();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Dialog
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    content: '',
    is_default: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      const res = await fetch(`${API}/api/admin/agreement-templates`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setTemplates(data);
      }
    } catch (error) {
      console.error('Şablonlar yüklenemedi:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNew = () => {
    setEditingTemplate(null);
    setFormData({
      name: '',
      content: '',
      is_default: false
    });
    setIsDialogOpen(true);
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      content: template.content,
      is_default: template.is_default
    });
    setIsDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name || !formData.content) {
      toast.error('Ad ve içerik zorunludur');
      return;
    }

    setIsSubmitting(true);
    try {
      const url = editingTemplate 
        ? `${API}/api/admin/agreement-templates/${editingTemplate.id}`
        : `${API}/api/admin/agreement-templates`;
      
      const res = await fetch(url, {
        method: editingTemplate ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });
      
      if (!res.ok) throw new Error('Kaydedilemedi');
      
      toast.success(editingTemplate ? 'Şablon güncellendi' : 'Şablon oluşturuldu');
      setIsDialogOpen(false);
      loadTemplates();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (template) => {
    if (template.is_default) {
      toast.error('Varsayılan şablon silinemez');
      return;
    }
    
    if (!window.confirm('Bu şablonu silmek istediğinize emin misiniz?')) return;
    
    try {
      const res = await fetch(`${API}/api/admin/agreement-templates/${template.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!res.ok) throw new Error('Silinemedi');
      
      toast.success('Şablon silindi');
      loadTemplates();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const insertPlaceholder = (placeholder) => {
    setFormData(prev => ({
      ...prev,
      content: prev.content + placeholder
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-templates-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold">Anlaşma Mesaj Şablonları</h1>
          <p className="text-muted-foreground mt-1">Müşterilere gönderilen anlaşma mesajlarını düzenleyin</p>
        </div>
        <Button onClick={handleNew}>
          <Plus className="h-4 w-4 mr-2" />
          Yeni Şablon
        </Button>
      </div>

      {/* Templates List */}
      <div className="space-y-4">
        {templates.map((template) => (
          <Card key={template.id} className={template.is_default ? 'border-primary' : ''}>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-primary" />
                  {template.name}
                  {template.is_default && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-primary/10 text-primary">
                      <Star className="h-3 w-3 fill-current" />
                      Varsayılan
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="icon" onClick={() => handleEdit(template)}>
                    <Edit className="h-4 w-4" />
                  </Button>
                  {!template.is_default && (
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="text-destructive hover:text-destructive"
                      onClick={() => handleDelete(template)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <pre className="whitespace-pre-wrap text-sm bg-muted/50 p-4 rounded-lg font-sans">
                {template.content}
              </pre>
            </CardContent>
          </Card>
        ))}

        {templates.length === 0 && (
          <div className="text-center py-12">
            <FileText className="h-12 w-12 mx-auto mb-4 text-muted-foreground/30" />
            <h3 className="text-lg font-medium mb-2">Henüz şablon yok</h3>
            <p className="text-muted-foreground mb-4">Yeni şablon ekleyerek başlayın</p>
          </div>
        )}
      </div>

      {/* Template Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingTemplate ? 'Şablonu Düzenle' : 'Yeni Şablon'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Şablon Adı</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Örn: Düğün Anlaşması"
              />
            </div>

            {/* Placeholders */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Info className="h-4 w-4" />
                Kullanılabilir Değişkenler
              </Label>
              <div className="flex flex-wrap gap-2">
                {placeholders.map((p) => (
                  <Button
                    key={p.key}
                    variant="outline"
                    size="sm"
                    onClick={() => insertPlaceholder(p.key)}
                    title={p.description}
                  >
                    {p.key}
                  </Button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Mesaj İçeriği</Label>
              <Textarea
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                placeholder="Mesaj içeriğini yazın..."
                rows={12}
                className="font-mono text-sm"
              />
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                checked={formData.is_default}
                onCheckedChange={(checked) => setFormData({ ...formData, is_default: checked })}
              />
              <Label className="cursor-pointer">Varsayılan şablon olarak ayarla</Label>
            </div>

            <div className="flex gap-3 pt-4">
              <Button variant="outline" onClick={() => setIsDialogOpen(false)} className="flex-1">
                İptal
              </Button>
              <Button onClick={handleSave} disabled={isSubmitting} className="flex-1">
                {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Kaydet'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
